import mammoth from "mammoth/mammoth.browser";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";

// Fichiers joints à une rédaction par l'IA : lus dans le navigateur pour
// l'aperçu, puis envoyés à l'IA. PDF et images partent tels quels ; Word,
// Excel, CSV et texte sont convertis ici en texte (structure gardée : titres,
// listes et tableaux en HTML pour Word, une section CSV par feuille Excel).

export const IA_TAILLE_MAX = 20 * 1024 * 1024; // comme les autres envois de MyALFA
export const IA_FICHIERS_MAX = 8;
export const IA_TEXTE_MAX = 600000; // caractères, limite du serveur pour une rédaction
export const IA_ACCEPT = ".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp";

const extension = (nom) => (/\.[A-Za-z0-9]{2,5}$/.exec(nom || "") || [""])[0].toLowerCase();
const sansExtension = (nom) => String(nom || "document").replace(/\.[A-Za-z0-9]{2,5}$/, "");
const fichierTexte = (texte, nom) => new File([texte], `${sansExtension(nom)}.txt`, { type: "text/plain" });

export const tailleLisible = (o) => (o < 1024 ? `${o} o` : o < 1048576 ? `${Math.round(o / 1024)} Ko` : `${(o / 1048576).toFixed(1)} Mo`);

// Libellé court affiché dans l'icône du fichier.
export const IA_GENRES = { pdf: "PDF", image: "IMG", word: "DOC", excel: "XLS", texte: "TXT" };

// Lit un fichier : { genre, apercu, envoi: { type, fichier }, caracteres }.
// Lève une erreur au message lisible si le fichier ne peut pas être utilisé.
export async function lireFichier(file) {
  const ext = extension(file.name);
  if (file.size > IA_TAILLE_MAX) throw new Error("plus de 20 Mo");
  if (ext === ".pdf") {
    return { genre: "pdf", apercu: { url: URL.createObjectURL(file) }, envoi: { type: "pdf", fichier: file }, caracteres: 0 };
  }
  if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) {
    return { genre: "image", apercu: { url: URL.createObjectURL(file) }, envoi: { type: "image", fichier: file }, caracteres: 0 };
  }
  if (ext === ".docx") {
    const { value } = await mammoth.convertToHtml({ arrayBuffer: await file.arrayBuffer() });
    // L'IA reçoit le texte structuré, sans les images intégrées (trop lourdes).
    const texte = value.replace(/<img[^>]*>/gi, "");
    if (!texte.replace(/<[^>]+>/g, "").trim()) throw new Error("document Word vide");
    return {
      genre: "word",
      apercu: { html: DOMPurify.sanitize(value) },
      envoi: { type: "texte", fichier: fichierTexte(texte, file.name) },
      caracteres: texte.length,
    };
  }
  if (ext === ".xlsx" || ext === ".xls") {
    const classeur = XLSX.read(await file.arrayBuffer(), { type: "array" });
    // Une feuille vide n'a pas de plage (« !ref ») : elle est ignorée.
    const feuilles = classeur.SheetNames.filter((nom) => classeur.Sheets[nom] && classeur.Sheets[nom]["!ref"]).map((nom) => ({
      nom,
      html: DOMPurify.sanitize(XLSX.utils.sheet_to_html(classeur.Sheets[nom], { header: "", footer: "" })),
      csv: XLSX.utils.sheet_to_csv(classeur.Sheets[nom], { blankrows: false }).trim(),
    })).filter((f) => f.csv);
    if (!feuilles.length) throw new Error("classeur Excel vide");
    const texte = feuilles.map((f) => `## Feuille « ${f.nom} »\n${f.csv}`).join("\n\n");
    return {
      genre: "excel",
      apercu: { feuilles: feuilles.map(({ nom, html }) => ({ nom, html })) },
      envoi: { type: "texte", fichier: fichierTexte(texte, file.name) },
      caracteres: texte.length,
    };
  }
  if ([".csv", ".txt", ".md"].includes(ext)) {
    const texte = await file.text();
    if (!texte.trim()) throw new Error("fichier vide");
    return { genre: "texte", apercu: { texte }, envoi: { type: "texte", fichier: fichierTexte(texte, file.name) }, caracteres: texte.length };
  }
  if (ext === ".doc") throw new Error("ancien format Word : enregistrez-le en .docx");
  throw new Error("format non accepté");
}

// Libère les aperçus (adresses locales des PDF et images).
export const libererApercu = (f) => {
  if (f && f.apercu && f.apercu.url) URL.revokeObjectURL(f.apercu.url);
};
